import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PageHeader } from "@/components/ui/page-header";
import { ReportMarkdownRenderer } from "@/components/ReportMarkdownRenderer";
import { 
  ArrowLeft, 
  FileText, 
  Building2, 
  Briefcase, 
  Calendar, 
  ExternalLink,
  Copy,
  CheckCircle,
  Loader2,
  Search,
  User
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface HiringManagerReport {
  id: string;
  job_url: string;
  company_name: string | null;
  job_title: string | null;
  report: string | null;
  hiring_manager: {
    name?: string;
    title?: string;
    linkedin?: string;
    email?: string;
  } | null;
  status: string | null;
  created_at: string | null;
  user_id: string | null;
}

export default function HiringManagerReports() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();
  const [reports, setReports] = useState<HiringManagerReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<HiringManagerReport | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReports();
  }, [user, isAdmin]);

  const fetchReports = async () => {
    if (!user) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('hiring_manager_reports')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Type cast the data since the JSONB field needs proper typing
      const typedData = (data || []).map(report => ({
        ...report,
        hiring_manager: report.hiring_manager as HiringManagerReport['hiring_manager']
      }));
      
      setReports(typedData);
    } catch (error: any) {
      console.error('Error fetching reports:', error);
      toast({
        title: "Error loading reports",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Report copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status: string | null) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-success text-white">Completed</Badge>;
      case 'pending':
        return <Badge className="bg-warning text-white">Pending</Badge>;
      case 'failed':
        return <Badge className="bg-destructive text-white">Failed</Badge>;
      default:
        return <Badge variant="outline">{status || 'Unknown'}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-2 text-muted-foreground">Loading reports...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Sign in Required</CardTitle>
            <CardDescription>
              Please sign in to view your hiring manager reports.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/auth')}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Detail View
  if (selectedReport) {
    return (
      <div className="min-h-screen bg-background">
        <PageHeader 
          title={selectedReport.job_title || "Hiring Manager Report"}
          description={selectedReport.company_name || undefined}
          onBack={() => setSelectedReport(null)}
        >
          <Button
            variant="outline"
            size="sm"
            onClick={() => copyToClipboard(selectedReport.report || '')}
            disabled={!selectedReport.report}
          >
            {copied ? <CheckCircle className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? 'Copied!' : 'Copy Report'}
          </Button>
          {selectedReport.job_url && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(selectedReport.job_url, '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Job Posting
            </Button>
          )}
        </PageHeader>

        <div className="container mx-auto p-6 max-w-5xl">
          {/* Hiring Manager Info Card */}
          {selectedReport.hiring_manager && Object.keys(selectedReport.hiring_manager).length > 0 && (
            <Card className="mb-6 border-primary/20 bg-primary/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-5 w-5 text-primary" />
                  Hiring Manager Found
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  {selectedReport.hiring_manager.name && (
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{selectedReport.hiring_manager.name}</p>
                    </div>
                  )}
                  {selectedReport.hiring_manager.title && (
                    <div>
                      <p className="text-sm text-muted-foreground">Title</p>
                      <p className="font-medium">{selectedReport.hiring_manager.title}</p>
                    </div>
                  )}
                  {selectedReport.hiring_manager.email && (
                    <div>
                      <p className="text-sm text-muted-foreground">Email</p>
                      <a href={`mailto:${selectedReport.hiring_manager.email}`} className="font-medium text-primary hover:underline">
                        {selectedReport.hiring_manager.email}
                      </a>
                    </div>
                  )}
                  {selectedReport.hiring_manager.linkedin && (
                    <div>
                      <p className="text-sm text-muted-foreground">LinkedIn</p>
                      <a 
                        href={selectedReport.hiring_manager.linkedin} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-medium text-primary hover:underline flex items-center gap-1"
                      >
                        View Profile <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Full Report */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Full Report
                </CardTitle>
                {getStatusBadge(selectedReport.status)}
              </div>
              <CardDescription>
                Generated on {formatDate(selectedReport.created_at)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {selectedReport.report ? (
                <ScrollArea className="h-[60vh]">
                  <div className="bg-card p-6 rounded-lg border">
                    <ReportMarkdownRenderer content={selectedReport.report} />
                  </div>
                </ScrollArea>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No report content available yet.</p>
                  {selectedReport.status === 'pending' && (
                    <p className="text-sm mt-2">The report is still being generated. Check back soon!</p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // List View
  return (
    <div className="min-h-screen bg-background">
      <PageHeader 
        title="Hiring Manager Reports"
        description={isAdmin ? "All user reports (Admin View)" : "Your saved hiring manager discovery reports"}
        backTo="/find-hiring-manager"
      >
        <Button onClick={() => navigate('/find-hiring-manager')}>
          <Search className="h-4 w-4 mr-2" />
          New Search
        </Button>
      </PageHeader>

      <div className="container mx-auto p-6 max-w-5xl">
        {reports.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground/50" />
              <h3 className="text-xl font-semibold mb-2">No Reports Yet</h3>
              <p className="text-muted-foreground mb-6">
                You haven't generated any hiring manager reports yet.
              </p>
              <Button onClick={() => navigate('/find-hiring-manager')}>
                <Search className="h-4 w-4 mr-2" />
                Find Your First Hiring Manager
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {reports.map((report) => (
              <Card 
                key={report.id}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setSelectedReport(report)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg truncate">
                          {report.job_title || 'Untitled Position'}
                        </h3>
                        {getStatusBadge(report.status)}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                        {report.company_name && (
                          <span className="flex items-center gap-1">
                            <Building2 className="h-4 w-4" />
                            {report.company_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {formatDate(report.created_at)}
                        </span>
                      </div>

                      {report.hiring_manager?.name && (
                        <div className="mt-3 flex items-center gap-2 text-sm">
                          <User className="h-4 w-4 text-success" />
                          <span className="text-success font-medium">
                            Found: {report.hiring_manager.name}
                            {report.hiring_manager.title && ` - ${report.hiring_manager.title}`}
                          </span>
                        </div>
                      )}
                    </div>

                    <Button variant="ghost" size="sm">
                      View Report
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
